import { useLoaderData } from "@tanstack/react-router";
import { ArrowLeft, Search, X } from "lucide-react";
import { useState } from "react";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export function DealValuesPage() {
  const { values, label } = useLoaderData({
    from: "/_authenticated/deal-values/$field" as any,
  }) as { values: string[]; label: string };

  const [search, setSearch] = useState("");

  const filtered = search
    ? values.filter((v) => v.toLowerCase().includes(search.toLowerCase()))
    : values;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <a href="/database-browser">
            <ArrowLeft className="h-4 w-4" />
          </a>
        </Button>
        <PageHeader
          title={label}
          description={`${values.length} distinct value${values.length !== 1 ? "s" : ""} from synced deals`}
        />
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={`Search ${label.toLowerCase()}...`}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            className="pl-9"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {search && (
          <span className="text-sm text-muted-foreground">
            {filtered.length} of {values.length}
          </span>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              <TableHead className="w-16 text-xs uppercase tracking-wider">#</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} className="h-24 text-center text-muted-foreground">
                  {search
                    ? "No values match your search."
                    : "No values found. Run a full sync first."}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((value, i) => (
                <TableRow key={value}>
                  <TableCell className="tabular-nums text-muted-foreground">{i + 1}</TableCell>
                  <TableCell className="font-medium">{value}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
